import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaGift, FaHeart, FaStar, FaCalendarAlt, FaPowerOff, FaCopy, FaTimes } from 'react-icons/fa';
import { RiSlideshow4Line } from 'react-icons/ri';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import Notiflix from 'notiflix';
import ModuleHeader from "../../moduleHeader";
import { getCurrentPackageUserModule, getCurrentPackageUser, setCurrentPackage } from "../../../services/api";
import { getNewYearWishList, getNewYearWishListBusiness, acceptNewYearWish, deleteNewYearWish, rejectNewYearWish, getNewYearWish, unlockNewYearWishWithTokens, deleteNewYearWishBusiness } from "../../../services/newYearWishService";
import NewYearSlide from "./newYearSlide";

export default function NewYearWishes({ data }) {
  const { id } = useParams();
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSlideView, setShowSlideView] = useState(false);
  const [isAutoSlideEnabled, setIsAutoSlideEnabled] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isWishReceivingEnabled, setIsWishReceivingEnabled] = useState(false);
  const [packageData, setPackageData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [currentPackageId, setCurrentPackageId] = useState(null);
  const [userCurrentPackage, setUserCurrentPackage] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [selectedWishDetail, setSelectedWishDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [showUnlockPopup, setShowUnlockPopup] = useState(false);
  const [selectedLockedWish, setSelectedLockedWish] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [wishToDelete, setWishToDelete] = useState(null);
  const slideIntervalRef = useRef(null);
  
  // Dilek gönderme linki
  const wishLink = `${window.location.origin}/${id}/`;

  useEffect(() => {
    Notiflix.Notify.init({
      position: 'top-right',
      timeout: 3000,
      clickToClose: true,
      pauseOnHover: true,
    });
  }, []);

  // Paket verisini çek
  useEffect(() => {
    if (!id) return;

    const fetchPackageData = async () => {
      try {
        const data = await getCurrentPackageUserModule(id, 17); // New Year Wishes moduleId: 17
        setPackageData(data);
        
        // Kullanıcının mevcut paketini kontrol et
        const userData = await getCurrentPackageUser(id);
        setUserCurrentPackage(userData.current_package);
        setCurrentPackageId(data.package?.id || null);
        setUserRole(userData.role); // Kullanıcı rolünü kaydet
        
        // Eğer mevcut paket bu modülse, toggle'ı aktif yap
        setIsWishReceivingEnabled(userData.current_package === (data.package?.id || null));
      } catch (error) {
        console.error("Paket verisi alınamadı:", error);
      } finally {
        setPageLoading(false);
      }
    };

    fetchPackageData();
  }, [id]);

  // Dilekleri yükle fonksiyonu (reusable)
  const loadWishes = async () => {
    try {
      setLoading(true);
      
      // Role göre doğru API'yi çağır
      let response;
      if (userRole === 'BUSINESS') {
        // BUSINESS rolü için özel API (locked olsa bile tüm içerikler gelir)
        response = await getNewYearWishListBusiness(20); // Son 20 dilek
      } else {
        // Normal kullanıcılar için standart API
        response = await getNewYearWishList();
      }
      
      if (response.success && response.data) {
        // Tarihe göre sırala (en yeni en üstte)
        const sortedWishes = [...response.data].sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB - dateA; // Descending order (en yeni en üstte)
        });
        setWishes(sortedWishes);
      }
    } catch (error) {
      console.error('Yılbaşı dilekleri yüklenirken hata:', error);
      Notiflix.Notify.failure('Dilekler yüklenirken bir hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  // Yeni yıl dileklerini çek
  useEffect(() => {
    // Role bilgisi yüklendikten sonra çağır
    if (userRole !== null) {
      loadWishes();
    }
  }, [userRole]);

  // Slideshow için dilekleri filtrele
  // BUSINESS kullanıcıları tüm mesajları görebilir, diğerleri sadece unlocked olanları
  const slideshowWishes = userRole === 'BUSINESS' 
    ? wishes 
    : wishes.filter(wish => wish.status === 'unlocked');

  // Yılbaşı geri sayımı
  useEffect(() => {
    const calculateCountdown = () => {
      const now = new Date();
      const newYear = new Date('2026-01-01T00:00:00');
      const diff = newYear - now;

      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        setCountdown({ days, hours, minutes, seconds });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleSlide = useCallback(() => {
    if (slideshowWishes.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % slideshowWishes.length);
  }, [slideshowWishes.length]);

  // Auto slide efekti
  useEffect(() => {
    if (isAutoSlideEnabled && slideshowWishes.length > 0 && showSlideView) {
      slideIntervalRef.current = setInterval(() => {
        handleSlide();
      }, 6000); // Her 6 saniyede bir kaydır (yarı yarıya yavaşlatıldı)
    } else {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
    }

    return () => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
    };
  }, [isAutoSlideEnabled, slideshowWishes.length, handleSlide, showSlideView]);

  const toggleAutoSlide = () => {
    setIsAutoSlideEnabled((prev) => !prev);
    if (!isAutoSlideEnabled) {
      setCurrentIndex(0);
    }
  };

  const openSlideView = () => {
    setCurrentIndex(0);
    setShowSlideView(true);
    // Sadece gösterilecek mesajlar varsa otomatik kaydırmayı başlat
    if (slideshowWishes.length > 0) {
      setIsAutoSlideEnabled(true);
    }
  };

  const closeSlideView = () => {
    setIsAutoSlideEnabled(false);
    setShowSlideView(false);
  };

  const toggleWishReceiving = async () => {
    try {
      // Aktif yapıyorsak 17 (New Year Wishes), pasif yapıyorsak 3
      const newPackageId = isWishReceivingEnabled ? 3 : 17;
      await setCurrentPackage(newPackageId);
      
      setIsWishReceivingEnabled(!isWishReceivingEnabled);
      setUserCurrentPackage(newPackageId);
      
      if (!isWishReceivingEnabled) {
        Notiflix.Notify.success('Dilek gönderimi aktif', {
          position: 'top-center',
          timeout: 3000,
        });
      } else {
        Notiflix.Notify.info('Dilek gönderimi pasif', {
          position: 'top-center',
          timeout: 3000,
        });
      }
    } catch (error) {
      console.error("Paket değiştirilirken hata:", error);
      Notiflix.Notify.failure("Bir hata oluştu!");
    }
  };

  const handleAcceptWish = async (uuid) => {
    try {
      await acceptNewYearWish(uuid);
      Notiflix.Notify.success("Dilek kabul edildi!");
      // Listeyi yenile
      loadWishes();
    } catch (error) {
      console.error("Dilek kabul edilirken hata:", error);
      Notiflix.Notify.failure("Dilek kabul edilirken bir hata oluştu!");
    }
  };

  const handleRejectWish = async (uuid) => {
    try {
      await deleteNewYearWish(uuid);
      Notiflix.Notify.success("Dilek silindi!");
      // Listeyi yenile
      loadWishes();
    } catch (error) {
      console.error("Dilek silinirken hata:", error);
      Notiflix.Notify.failure("Dilek silinirken bir hata oluştu!");
    }
  };

  const handleDeleteWishBusiness = async (id) => {
    try {
      await deleteNewYearWishBusiness(id);
      Notiflix.Notify.success("Dilek silindi!");
      loadWishes();
      setShowDeleteConfirm(false);
      setWishToDelete(null);
      setShowDetailPopup(false);
      setSelectedWishDetail(null);
    } catch (error) {
      console.error("Dilek silinirken hata:", error);
      const errorMessage = error.response?.data?.message || error.message || "Dilek silinirken bir hata oluştu!";
      Notiflix.Notify.failure(errorMessage);
    }
  };

  const openDeleteConfirm = (wish) => {
    setWishToDelete(wish);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setWishToDelete(null);
  };

  const handleWishClick = async (wish) => {
    // Kilitli ve BUSINESS değilse unlock popup'ı göster
    if (wish.status === 'locked' && userRole !== 'BUSINESS') {
      setSelectedLockedWish(wish);
      setShowUnlockPopup(true);
      return;
    }

    try {
      setLoadingDetail(true);
      setShowDetailPopup(true);
      const response = await getNewYearWish(wish.uuid);
      if (response.success && response.data) {
        setSelectedWishDetail(response.data);
      }
    } catch (error) {
      console.error("Dilek detayı alınırken hata:", error);
      Notiflix.Notify.failure("Dilek detayı yüklenirken bir hata oluştu!");
      setShowDetailPopup(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUnlockWish = async () => {
    if (!selectedLockedWish?.uuid) return;

    try {
      Notiflix.Loading.circle('Dilek açılıyor...');
      
      const response = await unlockNewYearWishWithTokens(selectedLockedWish.uuid);
      
      if (response.success) {
        Notiflix.Loading.remove();
        Notiflix.Notify.success('Dilek başarıyla açıldı! 🎉');
        
        // Popup'ı kapat
        setShowUnlockPopup(false);
        setSelectedLockedWish(null);
        
        // Listeyi yenile
        loadWishes();
      } else {
        throw new Error(response.message || 'Dilek açılamadı');
      }
    } catch (error) {
      Notiflix.Loading.remove();
      console.error('Dilek açılırken hata:', error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Dilek açılırken bir hata oluştu!';
      Notiflix.Notify.failure(errorMessage);
    }
  };

  const closeUnlockPopup = () => {
    setShowUnlockPopup(false);
    setSelectedLockedWish(null);
  };

  const closeDetailPopup = () => {
    setShowDetailPopup(false);
    setSelectedWishDetail(null);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(wishLink);
    Notiflix.Notify.success('Link kopyalandı!', {
      position: 'top-center',
      timeout: 2000,
    });
  };

  const shareLink = async () => {
    const shareData = {
      title: 'Yılbaşı Dileği İste',
      text: 'Yılbaşı için bana bir dilek göndermek ister misin?',
      url: wishLink,
    };

    // Native Share API'yi dene (mobil cihazlarda çalışır)
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        Notiflix.Notify.success('Link paylaşıldı!', {
          position: 'top-center',
          timeout: 2000,
        });
      } catch (error) {
        // Kullanıcı paylaşımı iptal etti veya hata oluştu
        if (error.name !== 'AbortError') {
          console.error('Paylaşım hatası:', error);
        }
      }
    } else {
      // Native share desteklenmiyorsa linki kopyala
      copyLink();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const unreadCount = wishes.filter((w) => !w.isRead).length;

  // Slide görünümü açıkken body scroll'unu engelle ve footer'ı gizle
  useEffect(() => {
    if (showSlideView) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('slide-view-active');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('slide-view-active');
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('slide-view-active');
    };
  }, [showSlideView]);

  // Loading screen
  if (pageLoading || !packageData) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Ana Liste Görünümü */}
      <div className={`min-h-screen bg-white transition-opacity duration-300 ${showSlideView ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        {/* ModuleHeader */}
        {packageData && (
          <div className="relative z-30 w-full mb-2">
            <ModuleHeader
              userName={
                packageData.user?.first_name + " " + packageData.user?.last_name
              }
              subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
              title={packageData.package?.name}
              description={packageData.package?.description}
              backgroundImage={packageData.package?.image_url}
              onExpandedChange={setIsHeaderExpanded}
              showToggle={true}
              autoHeight={true}
            />
          </div>
        )}

        {/* Header Section */}
        <div className="px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between gap-2 sm:gap-3 mb-4 sm:mb-6">
            {/* Küçük İstatistik Kartı */}
            {!loading && (
              <div className="bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] rounded-xl sm:rounded-2xl px-4 sm:px-5 py-2 sm:py-2.5 shadow-md">
                <div className="flex items-center gap-4 sm:gap-5 text-white">
                  <div className="flex items-center gap-2 sm:gap-2.5">
                    <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
                      <FaGift className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    </div>
                    <div>
                      <p className="text-white/80 text-[9px] sm:text-[10px] leading-tight">Toplam</p>
                      <p className="text-sm sm:text-base font-bold leading-tight">{wishes.length}</p>
                    </div>
                  </div>
                  {unreadCount > 0 && (
                    <>
                      <div className="w-px h-6 bg-white/30"></div>
                      <div className="text-right">
                        <p className="text-white/80 text-[9px] sm:text-[10px] leading-tight">Okunmamış</p>
                        <p className="text-sm sm:text-base font-bold leading-tight">{unreadCount}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Butonlar */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              {/* Dilek Gönderimi Aç/Kapa Butonu */}
              <button
                onClick={toggleWishReceiving}
                className={`px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg sm:rounded-xl transition-all duration-300 flex items-center gap-2 ${
                  isWishReceivingEnabled
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:shadow-lg hover:scale-105 active:scale-95'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:scale-105 active:scale-95'
                }`}
                title={isWishReceivingEnabled ? 'Dilek gönderimini kapat' : 'Dilek gönderimini aç'}
              >
                <span className="text-xs sm:text-sm font-medium">
                  {isWishReceivingEnabled ? 'Aktif' : 'Pasif'}
                </span>
                <FaPowerOff className={`w-4 h-4 sm:w-5 sm:h-5 ${isWishReceivingEnabled ? '' : 'opacity-60'}`} />
              </button>

              {/* Slideshow İkonu Butonu */}
              <button
                onClick={openSlideView}
                className="p-2 sm:p-2.5 rounded-lg sm:rounded-xl transition-all duration-300 bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] text-white hover:shadow-lg hover:scale-105 active:scale-95"
                title="Slideshow Görünümünde Aç"
              >
                <RiSlideshow4Line className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

        {/* Dilek Gönderme Linki - Sadece aktif olduğunda göster */}
        {isWishReceivingEnabled && (
          <div className="px-4 sm:px-6 mb-4 sm:mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-2xl p-5 shadow-lg">
              {/* Link Kutusu */}
              <div className="bg-white rounded-xl p-3 mb-3 border border-blue-200">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={wishLink}
                    readOnly
                    className="flex-1 text-xs sm:text-sm text-gray-700 bg-transparent border-none outline-none"
                  />
                </div>
              </div>

              {/* Paylaş Butonu */}
              <button
                onClick={shareLink}
                className="w-full bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] hover:from-[#2F8894] hover:to-[#4A9BA6] text-white rounded-xl py-3 px-4 flex items-center justify-center gap-2 font-medium transition-all duration-300 hover:shadow-lg active:scale-95"
              >
                <FaGift className="w-5 h-5" />
                <span>Arkadaşından Dilek İste</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3DA7B5]"></div>
          <span className="ml-3 text-gray-500 text-sm">Yükleniyor...</span>
        </div>
      )}

      {/* Wishes List */}
      {!loading && wishes.length === 0 && (
        <div className="px-6">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
            <div className="text-gray-400 text-5xl mb-4">🎁</div>
            <div className="text-gray-600 text-sm font-medium mb-1">
              Henüz yılbaşı dileği bulunmuyor
            </div>
            <div className="text-gray-400 text-xs">
              Size gönderilen dilekler burada görünecek
            </div>
          </div>
        </div>
      )}

      {!loading && wishes.length > 0 && (
        <div className="px-4 sm:px-6 pb-6 mb-20 space-y-3 sm:space-y-4">
          {wishes.map((wish) => {
            const isPending = wish.request_status === "pending";
            const isJodiacUser = wish.is_jodiac_user === 1;
            const isLocked = wish.status === "locked";
            const senderRole = wish.sender_role;
            const isAdmin = senderRole === 'ADMIN';
            const isUser = senderRole === 'USER';
            
            return (
              <div
                key={wish.uuid}
                onClick={() => handleWishClick(wish)}
                className={`bg-white border-2 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 shadow-md hover:shadow-lg transition-all duration-300 ${
                  isJodiacUser ? 'border-[#5abcc8]' : 'border-gray-200'
                } ${isPending ? 'bg-gradient-to-br from-blue-50/50 to-white' : ''} ${
                  (isLocked && userRole !== 'BUSINESS') ? '' : 'cursor-pointer'
                }`}
              >
                <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm ${
                        isPending
                          ? 'bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8]'
                          : 'bg-gradient-to-br from-gray-400 to-gray-500'
                      } ${isAdmin ? 'border-2 border-red-500' : isUser ? 'border-2 border-[#39a4b4]' : ''}`}
                    >
                      {getInitials(wish.sender_name)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1.5 sm:mb-2">
                      <div className="flex-1 min-w-0 text-left">
                        <h3 className="text-gray-800 font-semibold text-sm sm:text-base mb-0.5 sm:mb-1 text-left">
                          {wish.sender_name}
                        </h3>
                        {isJodiacUser && !isAdmin && !isUser && (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-[#5abcc8] text-white mb-1">
                            Jodiac Kullanıcısı
                          </span>
                        )}
                        <div className="flex items-center gap-1.5 sm:gap-2 text-gray-500 text-[10px] sm:text-xs justify-start">
                          <FaCalendarAlt className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                          <span className="truncate text-left">{formatDate(wish.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isAdmin && (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-500 text-white whitespace-nowrap">
                            Jodiac Kurucu
                          </span>
                        )}
                        {isUser && (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-[#39a4b4] text-white whitespace-nowrap">
                            Jodiac Üyesi
                          </span>
                        )}
                        {isPending ? (
                          <div className="flex-shrink-0">
                            <span className="bg-[#3DA7B5] text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium">
                              Yeni
                            </span>
                          </div>
                        ) : (isLocked && userRole !== 'BUSINESS') ? (
                          <div className="flex-shrink-0">
                            <span className="bg-gray-400 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium">
                              Kilitli
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {/* Başlık */}
                    {userRole === 'BUSINESS' || wish.status === 'unlocked' && (
                    <h4 className="text-gray-700 font-medium text-sm sm:text-base mb-1 text-left">
                      {wish.title}
                    </h4>
                    )}

                    {/* İçerik - BUSINESS kullanıcısı veya unlocked ise göster */}
                    {(userRole === 'BUSINESS' || wish.status === 'unlocked') && wish.content && (
                      <div className="mt-2">
                        <p className="text-gray-600 text-xs sm:text-sm leading-relaxed text-left line-clamp-3">
                          {wish.content}
                        </p>
                      </div>
                    )}

                    {/* Locked durumu */}
                    {userRole !== 'BUSINESS' && wish.status === 'locked' && (
                      <div className="mt-2">
                        <p className="text-gray-400 text-xs sm:text-sm italic text-left">
                          🔒 Bu dilek henüz kilitli
                        </p>
                      </div>
                    )}

                    {/* Butonlar */}
                    {isPending && (
                      <div className="mt-2 sm:mt-3 flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAcceptWish(wish.uuid);
                          }}
                          className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition-colors"
                        >
                          Kabul Et
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRejectWish(wish.uuid);
                          }}
                          className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors"
                        >
                          Sil
                        </button>
                      </div>
                    )}

                    {/* Decorative Elements */}
                    <div className="flex items-center justify-between mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <FaHeart className="w-3 h-3 sm:w-4 sm:h-4 text-red-400 flex-shrink-0" />
                        <span className="text-gray-400 text-[10px] sm:text-xs">Yılbaşı Dileği</span>
                      </div>
                      {wish.social_media && (
                        <span className="text-gray-500 text-[10px] sm:text-xs font-medium">
                          {wish.social_media}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>

      {/* Slide Görünümü - Tam Ekran Modal */}
      <NewYearSlide
        showSlideView={showSlideView}
        slideshowWishes={slideshowWishes.filter(wish => wish.request_status === 'accepted')}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        isAutoSlideEnabled={isAutoSlideEnabled}
        toggleAutoSlide={toggleAutoSlide}
        closeSlideView={closeSlideView}
        countdown={countdown}
        formatDate={formatDate}
        getInitials={getInitials}
        loadWishes={async () => {
          await loadWishes();
          // Yeni dilekler geldiğinde index'i sıfırla
          setCurrentIndex(0);
        }}
        refreshInterval={200} // Geri sayım süresi (saniye cinsinden)
      />

      {/* Silme Onay Modal */}
      <AnimatePresence>
        {showDeleteConfirm && wishToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={closeDeleteConfirm}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            >
              <div className="text-center mb-6">
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <FaTimes className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Dileği Sil</h3>
                <p className="text-gray-600 text-sm">
                  Bu dileği silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={closeDeleteConfirm}
                  className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => handleDeleteWishBusiness(wishToDelete.uuid || wishToDelete.id)}
                  className="flex-1 py-3 px-4 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors"
                >
                  Sil
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detay Popup */}
      <AnimatePresence>
        {showDetailPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={closeDetailPopup}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {loadingDetail ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3DA7B5]"></div>
                </div>
              ) : selectedWishDetail ? (
                <div className="p-6 sm:p-8">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] flex items-center justify-center text-white font-bold">
                          {getInitials(selectedWishDetail.sender_name)}
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-800">
                            {selectedWishDetail.sender_name}
                          </h3>
                          {selectedWishDetail.is_jodiac_user && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-[#5abcc8] text-white">
                              Jodiac Kullanıcısı
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <FaCalendarAlt className="w-4 h-4" />
                        <span>{formatDate(selectedWishDetail.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {userRole === 'BUSINESS' && selectedWishDetail.request_status === 'accepted' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteConfirm(selectedWishDetail);
                          }}
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
                        >
                          <FaTimes className="w-3.5 h-3.5" />
                          <span>Dileği Sil</span>
                        </button>
                      )}
                      <button
                        onClick={closeDetailPopup}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {selectedWishDetail.title}
                  </h2>

                  {/* Photo */}
                  {selectedWishDetail.photo_url && (
                    <div className="mb-6">
                      <img
                        src={selectedWishDetail.photo_url}
                        alt={selectedWishDetail.title}
                        className="w-full h-64 object-cover rounded-xl shadow-lg"
                      />
                    </div>
                  )}

                  {/* Content */}
                  {selectedWishDetail.content && (
                    <div className="mb-6">
                      <p className="text-gray-700 text-base leading-relaxed whitespace-pre-wrap">
                        {selectedWishDetail.content}
                      </p>
                    </div>
                  )}

                  {/* Status Info */}
                  <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                    <FaHeart className="w-5 h-5 text-red-400" />
                    <span className="text-gray-600 text-sm font-medium">
                      Yılbaşı Dileği
                    </span>
                    {selectedWishDetail.status === 'locked' && (
                      <span className="ml-auto bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                        🔒 Kilitli
                      </span>
                    )}
                    {selectedWishDetail.status === 'unlocked' && (
                      <span className="ml-auto bg-green-100 text-green-600 text-xs px-3 py-1 rounded-full">
                        ✓ Açıldı
                      </span>
                    )}
                  </div>
                </div>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Unlock Popup */}
      <AnimatePresence>
        {showUnlockPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeUnlockPopup}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-[#3DA7B5] p-5 text-center">
                <h3 className="text-xl font-bold text-white mb-1">Dilek Kilitli</h3>
                <p className="text-white/90 text-xs">
                  Bu dilek yeni yıla kadar kilitli
                </p>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                {/* Message */}
                <div className="text-center space-y-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-center gap-2 mb-2">

                      <p className="text-gray-700 text-xs leading-relaxed">
                        <span className="font-bold text-[#3DA7B5]">{selectedLockedWish?.sender_name}</span> sizin için güzel bir dilek hazırladı!
                      </p>
                    </div>
                    <p className="text-gray-600 text-xs">
                      Yeni yıl gecesi dileği görebileceksiniz.
                    </p>
                  </div>

                  <div className="flex items-center justify-center gap-1.5 text-gray-500 text-xs">
                    <FaCalendarAlt className="text-xs" />
                    <span>
                      Kilidi açılma: <span className="font-semibold">1 Ocak 2026</span>
                    </span>
                  </div>
                </div>

                {/* Unlock Option */}
                <div className="bg-[#E8F5F7] border-2 border-[#3DA7B5]/30 rounded-lg p-3">
                  <div className="text-center mb-3">
                    <h4 className="text-base font-bold text-gray-800 mb-2">Şimdi Açmak İster misin?</h4>
                    <div className="flex flex-col items-center gap-1 mb-2">
                      <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-[#3DA7B5]/20">
                        <svg className="w-5 h-5 text-[#3DA7B5]" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                        </svg>
                        <div className="flex flex-col items-start">
                          <span className="font-bold text-gray-800 text-base leading-none">10 JT</span>
                          <span className="text-[10px] text-gray-500 leading-none mt-0.5">Jodiac Token</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600">
                      ile hemen açabilirsiniz
                    </p>
                  </div>

                  <button
                    onClick={handleUnlockWish}
                    className="w-full py-2.5 bg-[#3DA7B5] hover:bg-[#2a8a96] active:bg-[#1f6b75] text-white font-bold text-sm rounded-lg transition-all duration-300 shadow-md flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                    </svg>
                    Hemen Aç
                  </button>
                </div>

                {/* Buy Token Link */}
                <div className="text-center pt-1">
                  <a
                    href="https://www.jodi.ac/token"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium underline"
                  >
                    Jodiac Token Satın Al →
                  </a>
                </div>

                {/* Close Button */}
                <button
                  onClick={closeUnlockPopup}
                  className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm rounded-lg transition-all duration-300"
                >
                  Kapat
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

